import React from 'react';
import { Users, Target, Lightbulb, Heart } from 'lucide-react';

const AboutPage: React.FC = () => {
  return (
    <div className="min-h-screen py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            About{' '}
            <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
              Immersa LLC
            </span>
          </h1>
          <div className="w-24 h-1 bg-gradient-to-r from-cyan-400 to-purple-400 rounded-full mx-auto"></div>
        </div>

        {/* Main Content */}
        <div className="space-y-12">
          {/* Mission Statement */}
          <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
                <Target className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white">Our Mission</h2>
            </div>
            <p className="text-lg text-gray-300 leading-relaxed">
              Immersa Edutainment is dedicated to bringing immersive VR experiences and STEAM workshops to our community. 
              Our mission is to inspire learning and fun through cutting-edge technology and interactive activities that 
              engage minds of all ages and backgrounds.
            </p>
          </div>

          {/* What We Offer */}
          <div className="bg-gradient-to-br from-cyan-900/30 to-teal-900/30 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-full flex items-center justify-center">
                <Lightbulb className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white">What We Offer</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-cyan-400">🥽 VR Experiences</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Immersive virtual reality games</li>
                  <li>• Educational VR simulations</li>
                  <li>• Multi-player VR adventures</li>
                  <li>• Flight and racing simulators</li>
                </ul>
              </div>
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-cyan-400">🔬 STEAM Workshops</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Science experiments and discovery</li>
                  <li>• Technology and programming classes</li>
                  <li>• Engineering design challenges</li>
                  <li>• Art and mathematics integration</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Our Values */}
          <div className="bg-gradient-to-br from-green-900/30 to-emerald-900/30 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white">Our Values</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🎯</span>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Innovation</h3>
                <p className="text-gray-300 text-sm">Pushing boundaries with cutting-edge technology and creative experiences.</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🤝</span>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Community</h3>
                <p className="text-gray-300 text-sm">Building connections and fostering collaboration among learners.</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🌱</span>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Growth</h3>
                <p className="text-gray-300 text-sm">Nurturing curiosity and lifelong learning for all ages.</p>
              </div>
            </div>
          </div>

          {/* Team Section */}
          <div className="bg-gradient-to-br from-orange-900/30 to-red-900/30 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white">Our Team</h2>
            </div>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              Our passionate team of educators, technologists, and experience designers work together to create 
              unforgettable learning adventures. We combine expertise in education, virtual reality, and 
              interactive entertainment to deliver experiences that are both fun and meaningful.
            </p>
            <div className="bg-white/5 rounded-lg p-6 border border-white/10">
              <p className="text-center text-gray-300 italic">
                "We believe that the best learning happens when you're having fun. Our goal is to make every 
                visit to Immersa an adventure in discovery."
              </p>
            </div>
          </div>

          {/* Future Vision */}
          <div className="text-center bg-gradient-to-br from-indigo-900/30 to-purple-900/30 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            <h2 className="text-3xl font-bold text-white mb-6">Looking to the Future</h2>
            <p className="text-lg text-gray-300 leading-relaxed">
              As we grow, we're committed to expanding our offerings, incorporating the latest technologies, 
              and building partnerships with educators and innovators. Our vision is to become the premier 
              destination for immersive learning experiences that inspire the next generation of thinkers, 
              creators, and problem-solvers.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;